import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay, map, catchError } from 'rxjs/operators';
import { CalendarEvent, EventFilters } from '../models/calendar-event.interface';

@Injectable({
  providedIn: 'root'
})
export class CalendarRepositoryService {
  private readonly STORAGE_KEY = 'calendar_events_v2';
  private readonly API_DELAY = 300; // Simular latencia de API

  constructor() {}


  /**
   * Obtiene todos los eventos del calendario
   */
  getEvents(): Observable<CalendarEvent[]> {
    return of(this.getEventsFromStorage()).pipe(
      delay(this.API_DELAY),
      map(events => events.map(event => this.parseEventDates(event)))
    );
  }

  /**
   * Obtiene eventos filtrados
   */
  getEventsFiltered(filters: EventFilters): Observable<CalendarEvent[]> {
    return this.getEvents().pipe(
      map(events => this.applyFilters(events, filters))
    );
  }

  /**
   * Obtiene un evento por ID
   */
  getEventById(id: string): Observable<CalendarEvent | null> {
    return this.getEvents().pipe(
      map(events => events.find(event => event.id === id) || null)
    );
  }

  /**
   * Crea un nuevo evento
   */
  createEvent(event: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>): Observable<CalendarEvent> {
    const newEvent: CalendarEvent = {
      ...event,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return of(newEvent).pipe(
      delay(this.API_DELAY),
      map(event => {
        this.saveEventToStorage(event);
        return event;
      })
    );
  }

  /**
   * Actualiza un evento existente
   */
  updateEvent(id: string, updates: Partial<CalendarEvent>): Observable<CalendarEvent> {
    return this.getEvents().pipe(
      map(events => {
        const eventIndex = events.findIndex(event => event.id === id);
        if (eventIndex === -1) {
          throw new Error(`Event with id ${id} not found`);
        }

        const updatedEvent: CalendarEvent = {
          ...events[eventIndex],
          ...updates,
          id,
          updatedAt: new Date()
        };

        events[eventIndex] = updatedEvent;
        this.saveEventsToStorage(events);
        return updatedEvent;
      }),
      delay(this.API_DELAY)
    );
  }

  /**
   * Elimina un evento
   */
  deleteEvent(id: string): Observable<boolean> {
    return this.getEvents().pipe(
      map(events => {
        const filteredEvents = events.filter(event => event.id !== id);
        this.saveEventsToStorage(filteredEvents);
        return true;
      }),
      delay(this.API_DELAY)
    );
  }

  /**
   * Reordena eventos
   */
  reorderEvents(events: CalendarEvent[]): Observable<CalendarEvent[]> {
    const reorderedEvents = events.map((event, index) => ({
      ...event,
      order: index
    }));

    return of(reorderedEvents).pipe(
      delay(this.API_DELAY),
      map(events => {
        this.saveEventsToStorage(events);
        return events;
      })
    );
  }

  private getEventsFromStorage(): CalendarEvent[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        let events: CalendarEvent[] = JSON.parse(stored);
        
        // Auto-upgrade emojis to new image paths for cached events
        const iconMap: { [key: string]: string } = {
          '💰': 'assets/images/icons/oportunidades/financing.png',
          '🏠': 'assets/images/icons/oportunidades/purchase.png',
          '📝': 'assets/images/icons/oportunidades/purchase.png',
          '🔨': 'assets/images/icons/oportunidades/inicio_reforma.png',
          '🛠️': 'assets/images/icons/oportunidades/inicio_reforma.png',
          '🧱': 'assets/images/icons/oportunidades/inicio_reforma.png',
          '📋': 'assets/images/icons/oportunidades/informe_1.png',
          '👷': 'assets/images/icons/oportunidades/informe_1.png',
          '🚧': 'assets/images/icons/oportunidades/informe_2.png',
          '🎨': 'assets/images/icons/oportunidades/informe_3.png',
          '🏗️': 'assets/images/icons/oportunidades/fin_obra.png',
          '🪧': 'assets/images/icons/oportunidades/fin_obra.png',
          '💵': 'assets/images/icons/oportunidades/venta.png',
          '💸': 'assets/images/icons/oportunidades/venta.png'
        };

        events = events.map(event => {
          if (event.icon && iconMap[event.icon]) {
            return { ...event, icon: iconMap[event.icon] };
          }
          return event;
        });
        
        return events;
      }
      return this.getDefaultEvents();
    } catch (error) {
      console.error('Error loading events from storage:', error);
      return this.getDefaultEvents();
    }
  }

  /**
   * Guarda un evento en localStorage
   */
  private saveEventToStorage(event: CalendarEvent): void {
    const events = this.getEventsFromStorage();
    const existingIndex = events.findIndex(e => e.id === event.id);
    
    if (existingIndex >= 0) {
      events[existingIndex] = event;
    } else {
      events.push(event);
    }
    
    this.saveEventsToStorage(events);
  }

  /**
   * Guarda todos los eventos en localStorage
   */
  private saveEventsToStorage(events: CalendarEvent[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(events));
    } catch (error) {
      console.error('Error saving events to storage:', error);
    }
  }

  /**
   * Limpia el localStorage del calendario (llamar al iniciar creación/edición nueva)
   */
  clearStorage(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Aplica filtros a los eventos
   */
  private applyFilters(events: CalendarEvent[], filters: EventFilters): CalendarEvent[] {
    let filteredEvents = [...events];

    if (filters.status && filters.status.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        filters.status!.includes(event.status)
      );
    }

    if (filters.type && filters.type.length > 0) {
      filteredEvents = filteredEvents.filter(event => 
        filters.type!.includes(event.type)
      );
    }

    if (filters.dateRange) {
      filteredEvents = filteredEvents.filter(event => 
        event.startDate >= filters.dateRange!.start && 
        event.endDate <= filters.dateRange!.end
      );
    }

    return filteredEvents.sort((a, b) => a.order - b.order);
  }

  /**
   * Convierte strings de fecha a objetos Date
   */
  private parseEventDates(event: any): CalendarEvent {
    return {
      ...event,
      startDate: new Date(event.startDate),
      endDate: new Date(event.endDate),
      createdAt: new Date(event.createdAt),
      updatedAt: new Date(event.updatedAt)
    };
  }

  /**
   * Genera un ID único
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Eventos por defecto del proyecto
   */
  private getDefaultEvents(): CalendarEvent[] {
    const now = new Date();
    const baseTime = now.getTime();
    
    const defaultEvents: (Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'> & { id?: string })[] = [
      {
        id: 'financiacion',
        title: 'Financiación',
        description: '¡Lo logramos! El proyecto ha alcanzado el 100% de financiación. Gracias a ti (y a unos cuantos más), esto ya es una realidad.',
        startDate: new Date(baseTime),
        endDate: new Date(baseTime + 1 * 24 * 60 * 60 * 1000),
        status: 'completed',
        type: 'financing',
        icon: 'assets/images/icons/oportunidades/financing.png',
        color: '#28a745',
        isEditable: true,
        order: 0,
        progress: 100
      },
      {
        id: 'compra',
        title: 'Compra',
        description: '¡Firmado y comprado! El inmueble ya es nuestro. Ahora sí empieza la cuenta atrás para hacerlo rentable….',
        startDate: new Date(baseTime + 10 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 15 * 24 * 60 * 60 * 1000),
        status: 'completed',
        type: 'purchase',
        icon: 'assets/images/icons/oportunidades/purchase.png',
        color: '#007bff',
        isEditable: true,
        order: 1,
        progress: 100
      },
      {
        id: 'reforma',
        title: 'Inicio de reforma',
        description: '¡Manos a la obra! Literalmente. Ya están entrando los primeros martillos, taladros de obra.',
        startDate: new Date(baseTime + 20 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 25 * 24 * 60 * 60 * 1000),
        status: 'in-progress',
        type: 'renovation',
        icon: 'assets/images/icons/oportunidades/inicio_reforma.png',
        color: '#ffc107',
        isEditable: true,
        order: 2,
        progress: 75
      },
      {
        id: 'informe1',
        title: 'Informe de obra 1',
        description: '¡Ya se nota el cambio! Aquí tienes el primer vistazo de la reforma. Todo sigue según lo planeado (y sí, todavía queda polvo)',
        startDate: new Date(baseTime + 28 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 30 * 24 * 60 * 60 * 1000),
        status: 'pending',
        type: 'report',
        icon: 'assets/images/icons/oportunidades/informe_1.png',
        color: '#17a2b8',
        isEditable: true,
        order: 3,
        progress: 0
      },
      {
        id: 'informe2',
        title: 'Informe de obra 2',
        description: 'Segunda parada en la ruta de la reforma. Las paredes ya no lloran, el suelo empieza a parecer suelo…',
        startDate: new Date(baseTime + 43 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 45 * 24 * 60 * 60 * 1000),
        status: 'pending',
        type: 'report',
        icon: 'assets/images/icons/oportunidades/informe_2.png',
        color: '#17a2b8',
        isEditable: true,
        order: 4,
        progress: 0
      },
      {
        id: 'informe3',
        title: 'Informe de obra 3',
        description: 'Últimos retoques a la vista. Pintura, acabados y ese toque “wow” que hará que este inmueble brille. ¡Ya huele a oportunidad (y a pintura)!',
        startDate: new Date(baseTime + 58 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 60 * 24 * 60 * 60 * 1000),
        status: 'pending',
        type: 'report',
        icon: 'assets/images/icons/oportunidades/informe_3.png',
        color: '#17a2b8',
        isEditable: true,
        order: 5,
        progress: 0
      },
      {
        id: 'finObra',
        title: 'Fin de obra',
        description: 'Reforma terminada. El inmueble ha pasado de patito feo a cisne urbano. Está listo para impresionar... y vender.',
        startDate: new Date(baseTime + 70 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 75 * 24 * 60 * 60 * 1000),
        status: 'pending',
        type: 'renovation',
        icon: 'assets/images/icons/oportunidades/fin_obra.png',
        color: '#6f42c1',
        isEditable: true,
        order: 6,
        progress: 0
      },
      {
        id: 'venta',
        title: 'Venta',
        description: '¡Vendida! La propiedad ha cambiado de manos y nos preparamos para el desenlace más bonito: recibir la rentabilidad.',
        startDate: new Date(baseTime + 90 * 24 * 60 * 60 * 1000),
        endDate: new Date(baseTime + 100 * 24 * 60 * 60 * 1000),
        status: 'pending',
        type: 'sale',
        icon: 'assets/images/icons/oportunidades/venta.png',
        color: '#dc3545',
        isEditable: true,
        order: 7,
        progress: 0
      }
    ];

    return defaultEvents.map((event, index) => ({
      ...event,
      id: event.id || this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date()
    }));
  }
}
